/**
 * StudioLogin.jsx — Standalone login page for Founderboard Studios admins.
 * Lives at /studio/login.  Visually distinct from the founder-facing /login:
 *   - "STUDIOS" eyebrow + tighter copy
 *   - Indigo studio accent + neutral dark canvas
 *   - On success → /studio/admin (NOT the founder dashboard)
 *   - Gated by studio_admin=true; non-admin accounts get a clear refusal
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import { Lock, Spinner, ArrowRight, Megaphone } from '@phosphor-icons/react';
import { StudioThemeProvider, StudioThemeToggle } from '../components/StudioTheme';
import GoogleSignInButton, { GOOGLE_SIGNIN_ENABLED } from '../components/GoogleSignInButton';
import OAuthIconButton, { OAUTH_PROVIDER_ENABLED } from '../components/OAuthProviderButtons';

const ACCENT = '#6366f1';

const StudioLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleStudioAuthSuccess = (user, token) => {
    const isAgency = !!(user.agency_id && user.agency_role && user.agency_active !== false);
    if (!user.studio_admin && !isAgency) {
      toast.error("This account doesn't have Studios access. Ask an owner to add you.");
      return;
    }
    if (user.studio_active === false && !isAgency) {
      toast.error('This Studios account has been deactivated. Reach out to an owner.');
      return;
    }
    // Persist token + user so the rest of the app can read it
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    onLogin?.(user, token);
    toast.success(`Welcome back, ${(user.full_name || user.email).split(' ')[0]}`);
    // Agency teammates land on the new marketplace workspace; legacy studio admins
    // still see the old /studio/admin shell until Sprint 5 retires it.
    navigate(isAgency ? '/agency' : '/studio/admin', { replace: true });
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    try {
      const r = await api.post('/auth/login', { email: email.trim(), password });
      handleStudioAuthSuccess(r.data.user || {}, r.data.token);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Login failed';
      if (msg.includes('UNVERIFIED_EMAIL')) {
        toast.error('Please verify your email first.');
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleGoogleCredential = async (credential) => {
    setBusy(true);
    try {
      const r = await api.post('/auth/google', { credential });
      if (r.data.new_user) {
        toast.error("This account doesn't have Studios access. Ask an owner to add you.");
        return;
      }
      handleStudioAuthSuccess(r.data.user || {}, r.data.token);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Google sign-in failed');
    } finally {
      setBusy(false);
    }
  };

  // Resolve a Microsoft/Zoho redirect that just bounced back from OAuthCallback.jsx
  useEffect(() => {
    if (!location.state?.oauthResult) return;
    const { oauthResult } = location.state;
    if (oauthResult.new_user) {
      toast.error("This account doesn't have Studios access. Ask an owner to add you.");
    } else {
      handleStudioAuthSuccess(oauthResult.user || {}, oauthResult.token);
    }
    window.history.replaceState({}, document.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StudioThemeProvider className="min-h-screen bg-[var(--s-bg)] text-[var(--s-text)] flex items-center justify-center p-4">
      <div className="absolute top-4 right-4 z-10"><StudioThemeToggle /></div>
      <div data-testid="studio-login-page" className="w-full flex items-center justify-center">
      {/* Subtle background grid (uses theme-aware --s-grid) */}
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(var(--s-grid) 1px, transparent 1px), linear-gradient(90deg, var(--s-grid) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />
      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-2.5 mb-8">
          <span
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT}aa)` }}
          >
            <Megaphone size={20} weight="fill" />
          </span>
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold" style={{ color: ACCENT }}>
              Founderboard
            </div>
            <div className="text-base font-heading font-bold leading-none" style={{ letterSpacing: '-0.01em' }}>
              Studios — Admin
            </div>
          </div>
        </div>

        <h1 className="text-3xl sm:text-4xl font-heading font-bold leading-[1.05] mb-3" style={{ letterSpacing: '-0.02em' }}>
          Sign in to <span style={{ color: ACCENT }}>Studios</span>
        </h1>
        <p className="text-sm text-[var(--s-text-muted)] mb-8 leading-relaxed">
          The backend for our creative team. Push deliverables to founder calendars,
          manage orders, and coordinate with the people you're catering to.
        </p>

        {(GOOGLE_SIGNIN_ENABLED || OAUTH_PROVIDER_ENABLED.microsoft || OAUTH_PROVIDER_ENABLED.zoho || OAUTH_PROVIDER_ENABLED.linkedin) && (
          <div className="mb-4">
            <div className="flex items-center justify-center gap-3">
              <GoogleSignInButton onCredential={handleGoogleCredential} disabled={busy} />
              <OAuthIconButton provider="microsoft" context="studio" loginPath="/studio/login" disabled={busy} />
              <OAuthIconButton provider="zoho" context="studio" loginPath="/studio/login" disabled={busy} />
              <OAuthIconButton provider="linkedin" context="studio" loginPath="/studio/login" disabled={busy} />
            </div>
            <div className="flex items-center gap-3 text-xs text-[var(--s-text-dim)] mt-4" aria-hidden="true">
              <div className="flex-1 h-px bg-[var(--s-border)]" />
              or
              <div className="flex-1 h-px bg-[var(--s-border)]" />
            </div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4" data-testid="studio-login-form">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-[var(--s-text-muted)] mb-2">
              Work email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@founderboard.io"
              data-testid="studio-login-email"
              autoComplete="email"
              required
              className="w-full bg-[var(--s-surface-2)] border border-[var(--s-border)] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[var(--s-border-strong)] placeholder:text-[var(--s-text-faint)]"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-[var(--s-text-muted)] mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              data-testid="studio-login-password"
              autoComplete="current-password"
              required
              className="w-full bg-[var(--s-surface-2)] border border-[var(--s-border)] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[var(--s-border-strong)] placeholder:text-[var(--s-text-faint)]"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="studio-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold text-white disabled:opacity-50 transition-opacity hover:opacity-90"
            style={{ backgroundColor: ACCENT }}
          >
            {busy ? <Spinner size={14} className="animate-spin" /> : <Lock size={14} weight="bold" />}
            Sign in
            {!busy && <ArrowRight size={14} weight="bold" />}
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-[var(--s-border)] text-[11px] text-[var(--s-text-dim)] leading-relaxed">
          Studios access is invite-only. Reach an owner (sid@ / lalith@) to be added —
          or if you're a founder, head over to the main <a href="/login" className="underline hover:text-[var(--s-text)]">app login</a>.
        </div>
      </div>
      </div>
    </StudioThemeProvider>
  );
};

export default StudioLogin;
